import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import skOutline from "@/data/sk-outline.json";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { CrmDashboardHeatmapResponse, CrmDashboardHeatmapStageMeta } from "@shared/api";

/** Match server-side SK bounds in place-geocode.ts */
const SK = { minLat: 47.65, maxLat: 49.7, minLng: 16.75, maxLng: 22.65 };

const MAP_W = 720;
const MAP_H = 380;
const PAD = 20;

const FALLBACK_STAGE_COLOR = "#64748b";

/** Must match server `HEAT_STAGE_COLORS` order for consistent fallback. */
const STAGE_HEATMAP_HEX = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#dc2626",
  "#9333ea",
  "#0891b2",
  "#c026d3",
  "#4f46e5",
  "#0d9488",
  "#ea580c",
  "#b91c1c",
  "#7c3aed",
  "#0284c7",
  "#65a30d",
  "#db2777",
] as const;

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const raw = hex.replace("#", "").trim();
  if (raw.length !== 6 && raw.length !== 3) return { r: 100, g: 116, b: 139 };
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return { r: 100, g: 116, b: 139 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function project(lat: number, lng: number): [number, number] {
  const x = PAD + ((lng - SK.minLng) / (SK.maxLng - SK.minLng)) * (MAP_W - 2 * PAD);
  const y = PAD + (1 - (lat - SK.minLat) / (SK.maxLat - SK.minLat)) * (MAP_H - 2 * PAD);
  return [x, y];
}

function outlinePathD(): string {
  const feat = skOutline as { geometry?: { coordinates?: number[][][] } };
  const ring = feat.geometry?.coordinates?.[0];
  if (!ring?.length) return "";
  const parts: string[] = [];
  for (let i = 0; i < ring.length; i++) {
    const [lng, lat] = ring[i];
    const [x, y] = project(lat, lng);
    parts.push(i === 0 ? `M ${x.toFixed(2)} ${y.toFixed(2)}` : `L ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  parts.push("Z");
  return parts.join(" ");
}

function drawHeatCanvas(
  canvas: HTMLCanvasElement,
  points: Array<{ x: number; y: number; count: number; color: string }>,
  maxCount: number,
) {
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  canvas.width = Math.floor(MAP_W * dpr);
  canvas.height = Math.floor(MAP_H * dpr);
  canvas.style.width = `${MAP_W}px`;
  canvas.style.height = `${MAP_H}px`;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, MAP_W, MAP_H);

  if (points.length === 0 || maxCount <= 0) return;

  ctx.globalCompositeOperation = "lighter";

  for (const p of points) {
    const t = Math.min(1, p.count / maxCount);
    const radius = 14 + t * 52;
    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
    const { r, g: gv, b } = hexToRgb(p.color);
    const a = 0.12 + t * 0.35;
    g.addColorStop(0, `rgba(${r},${gv},${b},${a})`);
    g.addColorStop(0.45, `rgba(${r},${gv},${b},${a * 0.45})`);
    g.addColorStop(1, `rgba(${r},${gv},${b},0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalCompositeOperation = "source-over";
}

function WidgetError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}

type Labeled = { label: string; labelHu: string; labelSk: string };

function pickLocalizedLabel(locale: string, o: Labeled): string {
  if (locale.startsWith("hu")) return o.labelHu || o.label;
  if (locale.startsWith("sk")) return o.labelSk || o.label;
  return o.label;
}

function stageColor(stage: CrmDashboardHeatmapStageMeta, fallbackIndex: number): string {
  if (stage.legendColor) return stage.legendColor;
  return STAGE_HEATMAP_HEX[fallbackIndex % STAGE_HEATMAP_HEX.length] ?? FALLBACK_STAGE_COLOR;
}

export function DashboardSkHeatmap({
  data,
  isLoading,
  isError,
  className,
  filtersActive,
}: {
  data: CrmDashboardHeatmapResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  className?: string;
  /** True when dashboard subpipeline / date filters apply (same cohort as other widgets). */
  filtersActive?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pathD = useMemo(() => outlinePathD(), []);

  const [visibleStageIds, setVisibleStageIds] = useState<Set<string>>(() => new Set());

  const pipelineList = useMemo(() => {
    if (!data) return [];
    return data.pipelines?.length ? data.pipelines : data.subpipelines ?? [];
  }, [data]);

  const sortedStages = useMemo(() => {
    if (!data?.stages.length) return [];
    const pipeLabel = (pid: string) => {
      const p = pipelineList.find((x) => x.pipelineId === pid);
      return p ? pickLocalizedLabel(locale, p) : pid;
    };
    const byPipe = new Map<string, CrmDashboardHeatmapStageMeta[]>();
    for (const s of data.stages) {
      const list = byPipe.get(s.pipelineId) ?? [];
      list.push(s);
      byPipe.set(s.pipelineId, list);
    }
    const pipeOrder = [...new Set(data.stages.map((s) => s.pipelineId))].sort((a, b) =>
      pipeLabel(a).localeCompare(pipeLabel(b), undefined, { sensitivity: "base" }),
    );
    const out: CrmDashboardHeatmapStageMeta[] = [];
    for (const pid of pipeOrder) {
      const list = byPipe.get(pid);
      if (list) out.push(...list);
    }
    return out;
  }, [data, pipelineList, locale]);

  const stageIdsKey = data?.stages.map((s) => s.stageId).join("|") ?? "";

  useEffect(() => {
    if (!stageIdsKey) {
      setVisibleStageIds(new Set());
      return;
    }
    setVisibleStageIds(new Set(stageIdsKey.split("|").filter(Boolean)));
  }, [stageIdsKey]);

  const stageById = useMemo(() => {
    const m = new Map<string, CrmDashboardHeatmapStageMeta>();
    for (const s of data?.stages ?? []) m.set(s.stageId, s);
    return m;
  }, [data?.stages]);

  const stageIndexById = useMemo(() => {
    const m = new Map<string, number>();
    sortedStages.forEach((s, i) => m.set(s.stageId, i));
    return m;
  }, [sortedStages]);

  const toggleStage = (stageId: string, checked: boolean | "indeterminate") => {
    setVisibleStageIds((prev) => {
      const next = new Set(prev);
      if (checked === true) next.add(stageId);
      else next.delete(stageId);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.points.filter((p) => visibleStageIds.has(p.stageId));
  }, [data, visibleStageIds]);

  const projected = useMemo(() => {
    return filtered.map((p) => {
      const [x, y] = project(p.lat, p.lng);
      const st = stageById.get(p.stageId);
      const idx = stageIndexById.get(p.stageId) ?? 0;
      const color = st ? stageColor(st, idx) : FALLBACK_STAGE_COLOR;
      return { x, y, count: p.count, label: p.label, color };
    });
  }, [filtered, stageById, stageIndexById]);

  const maxCount = useMemo(() => projected.reduce((m, p) => Math.max(m, p.count), 0), [projected]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    drawHeatCanvas(c, projected, maxCount);
  }, [projected, maxCount]);

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">{t("dashboard.heatmapTitle")}</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">{t("dashboard.heatmapSubtitle")}</p>
        {filtersActive ? (
          <p className="text-xs text-muted-foreground mt-1.5">{t("dashboard.heatmapFilterActiveNote")}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_min(100%,280px)] lg:items-start">
          <div className="space-y-3 min-w-0">
            {isLoading ? (
              <Skeleton className="w-full rounded-lg" style={{ height: MAP_H }} />
            ) : isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (
              <div className="relative w-full overflow-x-auto rounded-lg border bg-muted/20">
                <div className="relative inline-block min-w-[min(100%,720px)]" style={{ width: MAP_W, height: MAP_H }}>
                  <svg
                    width={MAP_W}
                    height={MAP_H}
                    className="absolute inset-0 text-foreground/25"
                    viewBox={`0 0 ${MAP_W} ${MAP_H}`}
                    aria-hidden
                  >
                    <path d={pathD} fill="hsl(var(--muted) / 0.35)" stroke="hsl(var(--border))" strokeWidth={1.25} />
                  </svg>
                  <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" width={MAP_W} height={MAP_H} />
                </div>
              </div>
            )}

            {!isLoading && !isError && data && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{t("dashboard.heatmapCitiesShown", { count: filtered.length })}</span>
                {data.dealsWithoutLocation > 0 ? (
                  <span>{t("dashboard.heatmapNoLocation", { count: data.dealsWithoutLocation })}</span>
                ) : null}
                {data.dealsGeocodedFailed > 0 ? (
                  <span>{t("dashboard.heatmapGeoFailed", { count: data.dealsGeocodedFailed })}</span>
                ) : null}
              </div>
            )}

            {!isLoading && !isError && data && visibleStageIds.size === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dashboard.heatmapEmptySelection")}</p>
            ) : null}
            {!isLoading && !isError && data && visibleStageIds.size > 0 && filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dashboard.heatmapEmptyStage")}</p>
            ) : null}
          </div>

          <div className="rounded-lg border bg-muted/15 p-3 min-w-0 lg:sticky lg:top-4">
            <Label className="text-xs font-medium text-muted-foreground">{t("dashboard.heatmapStageLegend")}</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">{t("dashboard.heatmapStageLegendHint")}</p>
            <ul className="max-h-[min(60vh,320px)] space-y-0.5 overflow-y-auto pr-1">
              {sortedStages.map((s, idx) => {
                const pid = s.pipelineId;
                const pipe = pipelineList.find((p) => p.pipelineId === pid);
                const pipeName = pipe ? pickLocalizedLabel(locale, pipe) : pid;
                const col = stageColor(s, idx);
                const checked = visibleStageIds.has(s.stageId);
                return (
                  <li key={s.stageId}>
                    <label
                      htmlFor={`heatmap-stage-${s.stageId}`}
                      className="flex cursor-pointer items-start gap-2 rounded-md py-1.5 px-1 hover:bg-muted/50"
                    >
                      <span
                        className="mt-1 h-3 w-3 shrink-0 rounded-sm border border-border/80 shadow-sm"
                        style={{ backgroundColor: col }}
                        aria-hidden
                      />
                      <Checkbox
                        id={`heatmap-stage-${s.stageId}`}
                        size="sm"
                        checked={checked}
                        onCheckedChange={(c) => toggleStage(s.stageId, c)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0 flex-1 text-sm leading-tight">
                        <span className="font-medium">{pickLocalizedLabel(locale, s)}</span>
                        <span className="block text-xs text-muted-foreground truncate">{pipeName}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
